import { Text, makeStyles } from '@rneui/themed'
import { useEffect, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { PressableOpacity } from './pressable_opacity'

interface LastUpdatedPillProps {
  lastUpdatedAt?: number // Timestamp in milliseconds
  isRefreshing?: boolean
  isProcessing?: boolean
  onPress?: () => void
}

export function LastUpdatedPill({
  isRefreshing = false,
  lastUpdatedAt,
  isProcessing = false,
  onPress,
}: LastUpdatedPillProps) {
  const [timeSinceLastUpdate, setTimeSinceLastUpdate] = useState<string>('')
  const styles = useStyles()

  useEffect(() => {
    const updateText = () => {
      if (!lastUpdatedAt) return
      const currentTime = Date.now()
      const differenceInSeconds = Math.floor(
        (currentTime - lastUpdatedAt) / 1000
      )
      if (differenceInSeconds < 60) {
        setTimeSinceLastUpdate(
          `${differenceInSeconds} sec${differenceInSeconds > 1 ? 's' : ''} ago`
        )
      } else {
        const differenceInMinutes = Math.floor(differenceInSeconds / 60)
        setTimeSinceLastUpdate(
          `${differenceInMinutes} min${differenceInMinutes > 1 ? 's' : ''} ago`
        )
      }
    }

    updateText()
    const interval = setInterval(
      updateText,
      lastUpdatedAt && Date.now() - lastUpdatedAt < 60000 ? 1000 : 60000
    )

    return () => clearInterval(interval)
  }, [lastUpdatedAt])

  return (
    <PressableOpacity onPress={onPress} style={styles.container}>
      {isRefreshing || isProcessing ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.text}>
            {isRefreshing
              ? 'Updating'
              : isProcessing
              ? 'Processing Changes'
              : ''}
          </Text>
          <ActivityIndicator
            animating={isProcessing || isRefreshing}
            size={'small'}
            color={styles.activity.color}
            style={styles.activity}
          />
        </View>
      ) : (
        <Text style={styles.text}>
          {`Last Updated: ${timeSinceLastUpdate}`}
        </Text>
      )}
    </PressableOpacity>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    borderRadius: 20,
    position: 'relative',
    alignSelf: 'flex-end',
    paddingHorizontal: 5,
    top: 10,
    right: 10,
    backgroundColor: theme.colors.white,
    borderWidth: 0,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 2,
    height: 25,
    zIndex: 1,
  },
  text: {
    fontSize: 12,
    color: theme.colors.grey3,
    textAlignVertical: 'center',
  },
  activity: { marginLeft: 5, color: theme.colors.primary },
  loadingContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))
